import React, { useState, useEffect } from 'react';
import "./GerenciarPrefeitura.css";
import { Button, message, Modal, Table, Space, DatePicker } from 'antd';
import { EditOutlined, EyeOutlined, PoweroffOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import dayjs from 'dayjs';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { TextField } from '@mui/material';
import api from '../../../../services/api';
import API_CONFIG from '../../../../config/api.config';

const GerenciarPrefeitura = () => {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const navigate = useNavigate();
  const [prefeituras, setPrefeituras] = useState([]);
  const [prefeiturasOriginais, setPrefeiturasOriginais] = useState([]);
  const [modalDetalhesVisivel, setModalDetalhesVisivel] = useState(false);
  const [prefeituraSelecionada, setPrefeituraSelecionada] = useState(null);
  const [prefeituraSelecionadaFiltro, setPrefeituraSelecionadaFiltro] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 600 });

  const [prefeiturasParaSelect, setPrefeiturasParaSelect] = useState([
    {
      value: null,
      label: 'Selecione a Prefeitura'
    }
  ]);

  useEffect(() => {
    carregarPrefeituras();
  }, []);

  const carregarPrefeituras = async () => {
    try {
      setLoading(true);
      const response = await api.get(API_CONFIG.ENDPOINTS.PREFEITURA.CONSULTAR_PREFEITURA);
      
      if (response.data.status) {
        const prefeiturasFormatadas = response.data.prefeituras.map(pref => ({
          id: pref.PRE_ID,
          nome: pref.PRE_NOME,
          dataAbertura: pref.PRE_DATA_ABERTURA,
          status: pref.PRE_STATUS === 'ATIVO' ? 'Ativo' : 'Inativo',
          endereco: `${pref.endereco?.END_LOGRADOURO || ''}, ${pref.endereco?.END_CIDADE || ''} - ${pref.endereco?.END_ESTADO || ''}`,
          telefone: pref.PRE_TELEFONE,
          email: pref.PRE_EMAIL,
          responsavel: pref.PRE_RESPONSAVEL,
          cnpj: pref.PRE_CNPJ
        }));
        setPrefeituras(prefeiturasFormatadas);
        setPrefeiturasOriginais(prefeiturasFormatadas);
        const prefeiturasSelect = [
          {
            value: null,
            label: 'Selecione a Prefeitura'
          },
          ...response.data.prefeituras.map(pref => ({
            value: pref.PRE_ID,
            label: pref.PRE_NOME
          }))
        ];
        setPrefeiturasParaSelect(prefeiturasSelect);
      } else {
        message.error('Erro ao carregar prefeituras');
      }
    } catch (error) {
      //console.error('Erro ao carregar prefeituras:', error);
      message.error('Erro ao carregar prefeituras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleMenuChange = (event) => {
      setIsMenuExpanded(!event.detail.isCollapsed);
    };
    window.addEventListener('menuStateChange', handleMenuChange);
    return () => window.removeEventListener('menuStateChange', handleMenuChange);
  }, []);

  const handleFiltrarPrefeitura = (idSelecionado) => {
    setPrefeituraSelecionadaFiltro(idSelecionado);
    if (!idSelecionado) {
      setPrefeituras(prefeiturasOriginais);
    } else {
      setPrefeituras(prefeiturasOriginais.filter(p => p.id === idSelecionado));
    }
  };

  const handleVisualizar = async (prefeitura) => {
    try {
      const response = await api.get(API_CONFIG.ENDPOINTS.PREFEITURA.CONSULTAR_PREFEITURA_ESPECIFICO(prefeitura.id));
      
      if (response.data.status) {
        const prefeituraDetalhada = response.data.prefeitura;
        setPrefeituraSelecionada({
          id: prefeituraDetalhada.PRE_ID,
          nome: prefeituraDetalhada.PRE_NOME,
          dataAbertura: prefeituraDetalhada.PRE_DATA_ABERTURA,
          status: prefeituraDetalhada.PRE_STATUS === 'ATIVO' ? 'Ativo' : 'Inativo',
          endereco: `${prefeituraDetalhada.endereco?.END_LOGRADOURO || ''}, ${prefeituraDetalhada.endereco?.END_CIDADE || ''} - ${prefeituraDetalhada.endereco?.END_ESTADO || ''}`,
          telefone: prefeituraDetalhada.PRE_TELEFONE,
          email: prefeituraDetalhada.PRE_EMAIL,
          responsavel: prefeituraDetalhada.PRE_RESPONSAVEL,
          cnpj: prefeituraDetalhada.PRE_CNPJ
        });
        setModalDetalhesVisivel(true);
      } else {
        message.error('Erro ao carregar detalhes da prefeitura');
      }
    } catch (error) {
      //console.error('Erro ao carregar detalhes da prefeitura:', error);
      message.error('Erro ao carregar detalhes da prefeitura');
    }
  };

  const handleEditar = (prefeitura) => {
    navigate(`/adm/editar-prefeitura/${prefeitura.id}`, { state: { prefeitura } });
  };

  const handleStatus = async (prefeitura) => {
    try {
      const novoStatus = prefeitura.status === 'Ativo' ? 'INATIVO' : 'ATIVO';
      const response = await api.put(API_CONFIG.ENDPOINTS.PREFEITURA.ATUALIZAR_PREFEITURA(prefeitura.id), {
        PRE_STATUS: novoStatus
      });

      if (response.data.status) {
        message.success(`Prefeitura ${novoStatus === 'ATIVO' ? 'ativada' : 'inativada'} com sucesso!`);
        carregarPrefeituras(); // Recarrega a lista para atualizar o status
      } else {
        message.error(response.data.message || 'Erro ao alterar status da prefeitura');
      }
    } catch (error) {
      //console.error('Erro ao alterar status da prefeitura:', error);
      message.error('Erro ao alterar status da prefeitura');
    }
  };

  const handleVoltar = () => {
    navigate('/adm');
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
    },
    {
      title: 'Data de Abertura',
      dataIndex: 'dataAbertura',
      key: 'dataAbertura',
      width: 180,
      render: (data) => dayjs(data).format('DD/MM/YYYY')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <span style={{
          color: status === 'Ativo' ? '#008026' : '#ff4d4f',
          fontWeight: 500
        }}>
          {status}
        </span>
      )
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleVisualizar(record)}
            className="visualizar-btn"
          >
            Visualizar
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditar(record)}
          >
            Editar
          </Button>
          <Button
            icon={<PoweroffOutlined />}
            onClick={() => handleStatus(record)}
            danger={record.status === 'Ativo'}
          >
            {record.status === 'Ativo' ? 'Inativar' : 'Ativar'}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className={`home-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
      <div className="modal-wrapper">
        <h1 className="modal-title">Gerenciar Prefeituras</h1>
        <div className="modal-content">
          <div className="search-container" style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', flexWrap: 'wrap' }}>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              style={{ marginBottom: 8 }}
            >
              <InputLabel id="select-prefeitura-label">Selecione a Prefeitura</InputLabel>
              <Select
                labelId="select-prefeitura-label"
                value={prefeituraSelecionadaFiltro}
                label="Selecione a Prefeitura"
                onChange={(e) => handleFiltrarPrefeitura(e.target.value)}
                disabled={loading}
              >
                {prefeiturasParaSelect.map(pref => (
                  <MenuItem key={pref.value || 'null'} value={pref.value}>
                    {pref.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              type="primary"
              onClick={handleFiltrarPrefeitura}
              className="search-button"
              style={{
                marginLeft: 4,
                marginBottom: 8,
                backgroundColor: '#001f4d',
                border: 'none',
                fontWeight: 500
              }}
              loading={loading}
            >
              BUSCAR
            </Button>
          </div>

          {/* Tabela responsiva */}
          {isMobile ? (
            <div className="tabela-mobile">
              {prefeituras.map((item) => (
                <div className="tabela-mobile-item" key={item.id}>
                  <div className="item-nome">{item.nome}</div>
                  <div className="item-quantidade">Status: {item.status}</div>
                  <div className="item-descricao">Endereço: {item.endereco}</div>
                  <div className="item-botoes">
                    <Button size="small" onClick={() => handleVisualizar(item)} className="visualizar-btn" loading={loading}>Visualizar</Button>
                    <Button size="small" onClick={() => handleEditar(item)} disabled={loading}>Editar</Button>
                    <Button size="small" danger={item.status === 'Ativo'} onClick={() => handleStatus(item)} loading={loading}>
                      {item.status === 'Ativo' ? 'Inativar' : 'Ativar'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={prefeituras}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              className="tabela-solicitacao"
              loading={loading}
            />
          )}
        </div>

        <Modal
          title="Detalhes da Prefeitura"
          open={modalDetalhesVisivel}
          onCancel={() => {
            setModalDetalhesVisivel(false);
            setPrefeituraSelecionada(null);
          }}
          afterClose={() => {
            setPrefeituraSelecionada(null);
          }}
          footer={null}
          width={800}
          centered
          className="prefeitura-modal"
          destroyOnClose={true}
          bodyStyle={{
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {prefeituraSelecionada && (
            <div className="detalhe-expandido-item" style={{ margin: 0, flex: 1, overflowY: 'auto' }}>
              <div className="col-esq">
                <div>
                  <span className="label-icone">Nome:</span>
                  <div className="valor-destaque">{prefeituraSelecionada.nome}</div>
                </div>

              </div>
              <div className="col-dir">
                <div>
                  <span className="label-icone">Endereço:</span>
                  <div className="valor-destaque">{prefeituraSelecionada.endereco}</div>
                </div>
                <div>
                  <span className="label-icone">Telefone:</span>
                  <div className="valor-destaque">{prefeituraSelecionada.telefone}</div>
                </div>
                <div>
                  <span className="label-icone">E-mail:</span>
                  <div className="valor-destaque">{prefeituraSelecionada.email}</div>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default GerenciarPrefeitura;